// types
import type { PlanUnit, PlanUnitMix } from 'components/types';
import type {
  AutomaticRosteringBlockReason,
  AutomaticRosteringState
} from './automaticRostering.types';

type BlockReasonInput = {
  /** The roster plan's current period; only a week can be generated. */
  period: string | null;
  /** The code of the one selected organizational unit, or null when that is not what is selected. */
  organizationalUnitCode: string | null;
  runState: AutomaticRosteringState;
  /**
   * Whether the planner may start a calculation at all. The start endpoint asks for
   * `ROSTER_PORTAL_ROSTER_PLAN_MODUS` on top of the module, which a read-only roster user does not
   * have - without this they would press an enabled button and be refused.
   */
  canStart: boolean;
};

/**
 * The code of the selected organizational unit, or null when the selection is not exactly one.
 *
 * A *group* of cost centres is null too, even one holding a single cost centre: the engine is
 * addressed by organizational unit code and a group has none of its own.
 */
export const getSelectedOrganizationalUnitCode = (
  selectedPlanUnits: PlanUnitMix[]
): string | null => {
  if (selectedPlanUnits.length !== 1) return null;

  const [unit] = selectedPlanUnits;
  if (typeof unit.uniqId !== 'number') return null;

  return (unit as PlanUnit).code ?? null;
};

/** A calculation the engine is still working on; asking for a second one makes no sense. */
export const isAutomaticRosteringInProgress = (
  state: AutomaticRosteringState
): boolean => state === 'starting' || state === 'CALCULATING';

/**
 * Whether there is a calculation for the engine to talk about.
 *
 * `idle` is the state before the status has been read at all; `NOT_STARTED` is the engine saying it
 * has nothing for this week. Either way there is nothing to explain, and an explanation panel that
 * can only answer "nothing has been calculated" is worse than a button that says so by being off.
 */
export const isAutomaticRosteringExplainable = (
  state: AutomaticRosteringState
): boolean => state !== 'idle' && state !== 'NOT_STARTED';

/**
 * A state a calculation only reaches after one was asked for.
 *
 * `idle` is "the status has not answered yet" and `NOT_STARTED` is the engine saying it has nothing
 * for this week; both are what the screen shows *before* a run, and the status query keeps reporting
 * the latter for the moment between starting and the first refetch. Treating either as an outcome is
 * how a run that is only just underway gets mistaken for one that has finished.
 */
export const isAutomaticRosteringOutcome = (
  state: AutomaticRosteringState
): boolean =>
  isAutomaticRosteringExplainable(state) &&
  !isAutomaticRosteringInProgress(state);

/**
 * Why the planner cannot start a run, or `null` when they can.
 *
 * One organizational unit at a time is a deliberate restriction, not a shortcut: the engine rosters
 * per site service and an explanation that spans units has no single thing to explain.
 */
export const getAutomaticRosteringBlockReason = ({
  period,
  organizationalUnitCode,
  runState,
  canStart
}: BlockReasonInput): AutomaticRosteringBlockReason | null => {
  if (isAutomaticRosteringInProgress(runState)) return 'running';
  if (!canStart) return 'noPlanModus';
  if (period !== 'week') return 'notWeekPeriod';
  if (!organizationalUnitCode) return 'noSinglePlanUnit';

  return null;
};
